import React, { useState } from 'react';
import * as styles from './Pagination.module.scss';
const { __ } = window.wp.i18n;

interface PaginationProps {
  totalPages: number;
  currentPage: number;
  onPageChange: (page: number) => void;
}

const Pagination: React.FC<PaginationProps> = React.memo(({ totalPages, currentPage, onPageChange }) => {
  const [displayPages, setDisplayPages] = useState<number[]>([]);
  const [totalDisplayPages, setTotalDisplayPages] = useState<number>(window.innerWidth >= 1024 ? 9 : window.innerWidth >= 430 ? 4 : 2);

  window.addEventListener('resize', function () {
    (window.innerWidth >= 1024) ? setTotalDisplayPages(9) : window.innerWidth >= 430 ? setTotalDisplayPages(4) : setTotalDisplayPages(2);
  })

  // Calculate visible page numbers based on current page and total pages
  const calculateDisplayPages = (setTotalDisplayPages: number) => {
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, startPage + setTotalDisplayPages)

    // Adjust startPage and endPage to always show 5/10 page numbers if possible
    if (endPage - startPage < setTotalDisplayPages) {
      startPage = Math.max(1, endPage - setTotalDisplayPages);
    }

    const pages = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
    setDisplayPages(pages);
  };

  // Initialize visible pages on first render
  React.useEffect(() => {
    calculateDisplayPages(totalDisplayPages);
  }, [currentPage, totalPages, totalDisplayPages]);

  const handleFirstPage = () => {
    onPageChange(1);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const handleLastPage = () => {
    onPageChange(totalPages);
  };

  const handlePageClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, page: number) => {
    onPageChange(page);
    e.preventDefault();
  };

  return (
    <div className={styles['pagination']}>
      <button onClick={handleFirstPage} disabled={currentPage === 1} className={[styles['first-page'], styles['arrow-button']].join(" ")}>
        {__('First Page', 'cg-jobs')}
      </button>
      <button onClick={handlePrevPage} disabled={currentPage === 1} className={[styles['previous'], styles['arrow-button']].join(" ")}>
        {__('Previous', 'cg-jobs')}
      </button>
      <div className={styles['pagination-container']}>
        {displayPages.map((pageNum) => (
          <button key={pageNum} onClick={(e) => handlePageClick(e, pageNum)} disabled={pageNum === currentPage}>
            {pageNum}
          </button>
        ))}
      </div>
      <button onClick={handleNextPage} disabled={currentPage === totalPages} className={[styles['next'], styles['arrow-button']].join(" ")}>
        {__('Next', 'cg-jobs')}
      </button>
      <button onClick={handleLastPage} disabled={currentPage === totalPages} className={[styles['last-page'], styles['arrow-button']].join(" ")}>
        {__('Last Page', 'cg-jobs')}
      </button>
    </div>
  );
});

export default Pagination;